import { useTranslation } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useEffect, useState, useRef, useCallback, forwardRef, type ComponentProps } from "react";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { estimateListUrl } from "@Features/estimation/api/estimation.api";
import {
  UpdateEstimateDataArgs,
  createNewEstimate,
  deleteEstimate,
  getEstimateNumberValueBasedOnSettings,
  isEstimateRequiredPermission,
  updateEstimateData,
} from "@Actions/estimation";
import { convertDate } from "@Services/luxonHelper";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { fetchCostDatabaseData } from "@Actions/portalSetting";
import { getDefaultCurrencyDetailForProjectLevel } from "@Actions/Currency";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import EstimateProperty from "./EstimateProperty";
import { useNavigate } from "react-router-dom";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { getEstimatePageUrl } from "@Services/utils/helpers";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import IconComponent from "@CubeComponents/icons";
import { PermissionFlow } from "@Features/estimation";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import _ from "lodash";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import Text from "@CubeComponents/html/Text/Text";
import SlickGrid1, { withDynamicColumns } from "@CubeComponents/html/SlickGrid1";
import type { GridClickEvent, SlickGrid1Ref, ToolbarItem } from "@CubeComponents/html/SlickGrid1";
import CentralProperties from "@CubeComponents/html/SlickGrid1/CentralProperties";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import {
  getEstimateListResponseSchema,
  type EstimateListItem as EstimateListItemBase,
  type EstimateListQuery,
  type GetEstimateListResponse,
} from "@ServerTypes/estimates/estimates.schema.js";
import type { CostDatabaseData } from "@ServerTypes/costDatabase/costDatabase.schema";
import type { RootState } from "src/reducers";
import type { AutocompleteRenderInputParams } from "@mui/material";
import type { Formatter } from "slickgrid-react";
import { defaultModulesMapper, folderPermissions } from "@Helpers/constants";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import { untranslated, type TranslationKey } from "@i18n";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
const { RESOURCE_MODIFY, RESOURCE_DELETE } = folderPermissions();

// Grid rows can include a synthetic placeholder ("__isDummy") rendered by SlickGrid
// for empty-state padding. Otherwise rows match the Zod-derived EstimateListItem.
type EstimateListItem = EstimateListItemBase & { __isDummy?: boolean };

/**
 * The grid handle this list forwards. `SlickGrid1Ref` is SlickGrid1's own published ref type, so the
 * instance the grid assigns and the handle a parent reads are one declaration: a hand-rolled subset
 * of it could only reach the grid's `ref` prop through an assertion, and it hid the holes the
 * DataView really returns (see `getAllSelectedItems` below).
 */
type EstimateGridRef = SlickGrid1Ref<EstimateListItem>;

/**
 * SlickGrid1's `ToolbarItem` calls the label `text`, but its `ToolBar.tsx` renders `item.name`.
 * Every caller in this repo passes `name`, so the label field is intersected in here rather than
 * switching to `text` (which would render an empty button).
 */
type EstimateToolbarItem = ToolbarItem & { name: string };

type CurrencyDetail = {
  id?: string;
  name?: string;
  category?: "Prefix" | "Suffix";
  selectedSymbol?: { id?: string; name: string };
};

type CurrencyDataState = {
  currencyDetail?: CurrencyDetail;
};

type EditingRowValue = {
  estimateId?: ObjectIdString;
  name?: string;
  description?: string | null;
  estimateType?: string;
};

type ProjectDataLite = {
  _id?: string;
  description?: string;
};

type PortalDataLite = {
  _id?: string;
  name?: string;
};

/**
 * The user/role map `PermissionFlow` emits, taken from its own prop so the two cannot drift apart.
 * The values are the NUMERIC `folderRoles()` ids (or `""` while no role is picked), which is what
 * `usersRolesPayload` below converts with `Number()` before the create call.
 */
type EstimateUsersRoles = Parameters<ComponentProps<typeof PermissionFlow>["getUsersCallBack"]>[0];

interface EstimateListProps {
  projectId?: ObjectIdString;
  showOnlyGrid?: boolean;
  portalList?: boolean;
  query: EstimateListQuery;
  spaceId?: ObjectIdString;
  allowSingleSelection?: boolean;
  linkEstimateButtonStateEnable?: (state: boolean) => void;
}

const EstimateList = forwardRef<EstimateGridRef, EstimateListProps>(
  (
    {
      projectId,
      showOnlyGrid = false,
      portalList = false,
      query,
      spaceId,
      allowSingleSelection,
      linkEstimateButtonStateEnable,
    }: EstimateListProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [currencyData, setCurrencyData] = useState<CurrencyDataState>({});
    const innerRef = useRef<EstimateGridRef | null>(null);
    // A ForwardedRef is one of three things: an object ref, a CALLBACK ref, or null when the parent
    // passed none. SlickGrid1 assigns its instance straight onto the ref object it is handed
    // (`gridRef.current = reactGrid`), so only the object arm can be forwarded; anything else falls
    // back to the internal ref — the same rule the grid itself applies to its own ref.
    const dataGrid = ref !== null && typeof ref === "object" ? ref : innerRef;
    const currencyDataRef = useRef<CurrencyDataState>(currencyData);
    let queryParams = query;
    if (spaceId) {
      queryParams = {
        ...queryParams,
        spaceId,
        params: {
          ...(queryParams.params || {}),
          spaceId,
        },
      };
    }
    const buttonInitialState = {
      create: false,
      edit: true,
      delete: true,
      update: true,
      cancel: true,
      properties: false,
    };
    const [estimateCostIndex, setEstimateCostIndex] = useState<number | string>(1);
    const [openPropertySidebar, setOpenPropertySidebar] = useState(false);
    const [estimatePropertyDetail, setEstimatePropertyDetail] = useState<EstimateListItem | null>(null);
    const [selectedRowsCount, setSelectedRowsCount] = useState(0);
    const [estimateName, setEstimateName] = useState("");
    // "" is the pre-selection placeholder; the ids themselves are 24-hex ObjectIds.
    const [estimateSpaceId, setEstimateSpaceId] = useState<ObjectIdString | "">("");
    const [estimateCostDatabaseId, setEstimateCostDatabaseId] = useState<ObjectIdString | "">("");
    const [estimateDescription, setEstimateDescription] = useState("");
    const [openCreatePopUp, setOpenCreatePopUp] = useState(false);
    const [inheritPermission, setInheritPermission] = useState(true);
    const [showPermissionsFlow, setShowPermissionsFlow] = useState(false);
    const [usersRoles, setUsersRoles] = useState<EstimateUsersRoles>({});
    const [editingRowValue, setEditingRowValue] = useState<EditingRowValue>({});
    const [getEstimateListQuery] = useState(queryParams);
    const [toolbarButtonDisableState, setToolbarButtonDisableState] = useState(buttonInitialState);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const editingRowValueRef = useRef<EditingRowValue>(editingRowValue);

    editingRowValueRef.current = editingRowValue;

    // ButtonComponent already uses forwardRef<any, any>, but it doesn't formally declare
    // data-testid; passing through React.ComponentProps lets us add data-testid without `any`.
    const ButtonComponentWithTestId = ButtonComponent as React.ComponentType<
      React.ComponentProps<typeof ButtonComponent> & { "data-testid"?: string }
    >;

    const toolbarOptions: EstimateToolbarItem[] = [
      ...(portalList
        ? []
        : [
            {
              id: "create",
              name: t("Create"),
              prefixIcon: "add",
              disabled: toolbarButtonDisableState.create,
            },
            {
              id: "edit",
              name: t("Edit"),
              prefixIcon: "edit",
              disabled: toolbarButtonDisableState.edit,
            },
            {
              id: "delete",
              name: t("Delete"),
              prefixIcon: "delete",
              disabled: toolbarButtonDisableState.delete,
            },
            {
              id: "update",
              name: t("Update"),
              prefixIcon: "Update",
              disabled: toolbarButtonDisableState.update,
            },
            {
              id: "cancel",
              name: t("Cancel"),
              prefixIcon: "Cancel",
              disabled: toolbarButtonDisableState.cancel,
            },
          ]),
    ];
    const dispatch = useDispatch();
    const projectData = useSelector((state: RootState) => state.projectData) as ProjectDataLite;
    const portalData = useSelector((state: RootState) => state.portalData) as PortalDataLite;

    const init = useCallback(async () => {
      dispatch(fetchCostDatabaseData({ value: { projectId } }));
      const response = await getDefaultCurrencyDetailForProjectLevel({
        projectId: projectId!,
      });
      if (response.data.status) {
        const currency = {
          ...response.data.currencyDetail,
          selectedSymbol: response.data.symbolDetail,
        };
        setCurrencyData({
          currencyDetail: currency,
        });
      }
    }, [projectId]);

    useEffect(() => {
      if (projectId) {
        init();
      }
    }, [init, projectId]);

    useEffect(() => {
      if (projectData._id) {
        document.title = `Estimates - ${projectData.description} `;
      } else if (portalList && portalData._id) {
        document.title = `Estimates - ${portalData.name} `;
      }
    }, [portalData, projectData, portalList]);

    useEffect(() => {
      currencyDataRef.current = currencyData;
    }, [currencyData]);

    const costDatabaseList = useSelector((state: RootState) => state.getCostDatabaseData) as CostDatabaseData[];

    const cancelEditRecord = () => {
      setEditingRowValue({});
      setToolbarButtonDisableState(buttonInitialState);
      dataGrid.current?.slickGrid.setSelectedRows([]);
    };

    const updateClickHandler = async () => {
      const name = editingRowValue.name?.trim() ?? "";
      if (!name || !editingRowValue.estimateId) {
        dispatch(showToastNotification({ content: t("Estimate name is required"), type: TOAST_TYPE_ERROR }));
      } else {
        setToolbarButtonDisableState((prev) => ({
          ...prev,
          update: true,
          cancel: true,
        }));
        const updateParams: UpdateEstimateDataArgs = {
          estimateId: editingRowValue.estimateId,
          name,
        };
        if (editingRowValue.estimateType !== "merge") {
          updateParams.description = editingRowValue.description;
        }
        const response = (await updateEstimateData(updateParams)) as { data: GetEstimateListResponse };
        if (response.data.status) {
          const content = t("edit_estimate", { estimate_name: name });
          dispatch(showToastNotification({ content, type: TOAST_TYPE_SUCCESS }));
          cancelEditRecord();
          dataGrid.current?.refresh?.();
        } else {
          dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
          setToolbarButtonDisableState((prev) => ({
            ...prev,
            update: false,
            cancel: false,
          }));
        }
      }
    };

    const toolbarClickHandler = (toolClicked: string) => {
      if (!dataGrid.current) {
        return;
      }
      // @slickgrid-universal/common declares `getAllSelectedItems(): T[]`, but its implementation
      // pushes `getItemById(id)` for every id still in `selectedRowIds` — that is `undefined` for a
      // selection kept across a refresh/filter, so the holes below are real. Widened on assignment
      // (never asserted) to keep the guard honest.
      const selectedItems: Array<EstimateListItem | undefined> = dataGrid.current.dataView.getAllSelectedItems();
      const selectedRecords = selectedItems.filter(
        (record): record is EstimateListItem => record != null && !record.__isDummy,
      );
      switch (toolClicked) {
        case "create":
          setOpenCreatePopUp(true);
          break;

        case "edit": {
          if (selectedRecords.length == 1) {
            const selectedRecord = selectedRecords[0];
            if (!selectedRecord) {
              break;
            }
            setEditingRowValue({
              estimateId: selectedRecord._id,
              name: selectedRecord.name,
              description: selectedRecord.description,
              estimateType: selectedRecord.estimateType,
            });
            setToolbarButtonDisableState((prev) => ({
              ...prev,
              create: true,
              edit: true,
              delete: true,
              update: false,
              cancel: false,
              properties: true,
            }));
          }
          break;
        }

        case "delete": {
          setOpenDeleteModal(true);
          break;
        }

        case "update": {
          updateClickHandler();
          break;
        }

        case "cancel": {
          cancelEditRecord();
          break;
        }

        case "properties": {
          setOpenPropertySidebar((prev) => !prev);
          break;
        }
      }
    };

    const updateStateToRefreshDatasource = () => {
      // `refresh` is one of the extras SlickGrid1 assigns onto the instance after it mounts, so it
      // is optional on the ref type until then.
      dataGrid.current?.refresh?.();
    };

    const recordClick = (args: GridClickEvent) => {
      if (!editingRowValueRef.current.estimateId && !portalList && !showOnlyGrid && args.detail.args.cell !== 0) {
        // `getDataItem` is declared `any` by slickgrid and returns nothing for a row index the view
        // no longer holds, so the row is taken at the type this grid binds and stays optional.
        const rowData: EstimateListItem | undefined = dataGrid.current?.slickGrid.getDataItem(args.detail.args.row);
        const projectIdValue = projectId ?? "";
        if (rowData?._id && projectIdValue) {
          navigate(getEstimatePageUrl(projectIdValue, String(rowData._id)));
        }
      }
    };

    const selectionEvent = () => {
      if (!dataGrid.current) {
        return;
      }

      // Holes are possible here too — see the note in toolbarClickHandler above.
      const selectedItems: Array<EstimateListItem | undefined> = dataGrid.current.dataView.getAllSelectedItems();
      const totalRowsSelected = selectedItems.filter(
        (record): record is EstimateListItem => record != null && !record.__isDummy,
      );

      setSelectedRowsCount(totalRowsSelected.length);

      if (linkEstimateButtonStateEnable) {
        linkEstimateButtonStateEnable(totalRowsSelected.length === 1);
        return;
      }

      if (totalRowsSelected.length === 1) {
        const selectedRecord = totalRowsSelected[0]!;
        const isEditPermission = isEstimateRequiredPermission(selectedRecord, RESOURCE_MODIFY.id);
        const isDeletePermission = isEstimateRequiredPermission(selectedRecord, RESOURCE_DELETE.id);

        setEstimatePropertyDetail(selectedRecord);
        setToolbarButtonDisableState((prev) => ({
          ...prev,
          edit: !isEditPermission,
          delete: !isDeletePermission,
        }));
      } else {
        setEstimatePropertyDetail(null);
        setToolbarButtonDisableState((prev) => ({
          ...prev,
          edit: true,
          delete: true,
        }));
      }
    };

    const createEstimate = async () => {
      if (!estimateName.trim() || !estimateSpaceId || !projectId) {
        return;
      }
      if (estimateCostDatabaseId && (!Number.isFinite(+estimateCostIndex) || +estimateCostIndex <= 0)) {
        dispatch(
          showToastNotification({
            content: t("Please enter a positive value for Cost Index."),
            type: TOAST_TYPE_WARNING,
          }),
        );
        return;
      }

      const usersRolesPayload = Object.entries(usersRoles).reduce<Record<string, number>>((acc, [userId, roleId]) => {
        const numericRoleId = Number(roleId);
        if (Number.isFinite(numericRoleId)) {
          acc[userId] = numericRoleId;
        }
        return acc;
      }, {});

      const response = await createNewEstimate({
        name: estimateName,
        description: estimateDescription,
        spaceId: estimateSpaceId,
        projectId,
        inheritPermission,
        costDatabaseId: estimateCostDatabaseId || undefined,
        costIndex: estimateCostDatabaseId ? Number(estimateCostIndex) : undefined,
        usersRoles: !inheritPermission ? usersRolesPayload : undefined,
      });
      if (response.data.status) {
        const content = t("add_estimate", { estimate_name: estimateName });
        dispatch(showToastNotification({ content, type: TOAST_TYPE_SUCCESS }));
        dataGrid.current?.refresh?.();
      } else {
        dispatch(
          showToastNotification({
            content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      cancelCreateModal();
    };

    const handleCreateEstimate = () => {
      if (inheritPermission || showPermissionsFlow) {
        createEstimate();
      } else {
        setShowPermissionsFlow(true);
      }
    };

    const formatDate: Formatter<EstimateListItem> = (_row, _cell, _value, _columnDef, props) => {
      const container = document.createElement("div");
      const root = createRoot(container);
      const dateFormat = convertDate(Number(props.createdDate), "dateForTooltip");
      root.render(
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
            <span>{convertDate(Number(props.createdDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>,
      );
      return container;
    };

    const estimateTotalTemplate: Formatter<EstimateListItem> = (_row, _cell, _value, _columnDef, params) => {
      const container = document.createElement("div");
      const root = createRoot(container);

      root.render(
        // `amount` is `z.number().optional()` on estimateListItemSchema, so the row never carries a
        // null here — the symbols are shown whenever the project has a currency configured.
        currencyDataRef.current.currencyDetail?.category ? (
          <>
            {currencyDataRef.current.currencyDetail.category === "Prefix"
              ? currencyDataRef.current.currencyDetail.selectedSymbol?.name
              : null}{" "}
            {getEstimateNumberValueBasedOnSettings(Number(params.amount ?? 0), params, "amount")}{" "}
            {currencyDataRef.current.currencyDetail.category === "Suffix"
              ? currencyDataRef.current.currencyDetail.selectedSymbol?.name
              : null}
          </>
        ) : (
          <>{getEstimateNumberValueBasedOnSettings(Number(params.amount ?? 0), params, "amount")}</>
        ),
      );

      return container;
    };

    const getCostDatabaseDropdown = useCallback(
      () => (
        <AutoComplete
          options={costDatabaseList}
          valueField="_id"
          width={"100%"}
          getOptionLabel={(option: CostDatabaseData) => option.versionData?.name || ""}
          value={costDatabaseList.find((opt: CostDatabaseData) => opt._id === estimateCostDatabaseId) || undefined}
          onChange={(_event: React.SyntheticEvent, newValue: CostDatabaseData | null) => {
            // CostDatabaseData._id is declared z.unknown() in costDatabase.schema.ts (it can arrive
            // as a BSON ObjectId), so this is the one point where the value enters the type system.
            // Validated rather than tagged: the brand only means anything if the hex actually ran.
            const parsedId = objectIdString.safeParse(newValue?._id);
            setEstimateCostDatabaseId(parsedId.success ? parsedId.data : "");
          }}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextFieldComponent {...params} placeholder={t("Select Cost Database")} />
          )}
        />
      ),
      [costDatabaseList, estimateCostDatabaseId],
    );

    const cancelCreateModal = () => {
      setOpenCreatePopUp(false);
      setEstimateDescription("");
      setEstimateName("");
      setEstimateSpaceId("");
      setEstimateCostDatabaseId("");
      setEstimateCostIndex("1");
      setShowPermissionsFlow(false);
      setInheritPermission(true);
    };

    const handlePermission = () => {
      setInheritPermission((prev) => !prev);
    };

    const createEstimateContent = () => (
      <div className="flex gap-5 flex-col">
        <div className="flex gap-4">
          <div className="flex min-w-32 items-center flex-1 text-base font font-semibold ">
            <Text>{t("Name")}</Text>
            <span className="text-2xl align-middle font-light text-red-500">*</span>
          </div>
          <div className="flex flex-4">
            <TextFieldComponent
              type="text"
              placeholder={t("Estimate Name")}
              value={estimateName}
              onChange={(event) => setEstimateName(event.target.value)}
              slotProps={{ htmlInput: { minLength: 1, maxLength: 255, "data-testid": "nameInputField" } }}
            />
          </div>
        </div>
        {/* <div className="flex gap-4">
          <div className="flex items-center min-w-32 flex-1 text-base font font-semibold">
            {t("Space")}
            <span className="text-2xl align-middle font-light text-red-500">*</span>
          </div>
          <div className="flex flex-4 border-system_colour_37" data-testid="spaceDropdownField">
            {getSpacesDropdown()}
          </div>
        </div> */}
        <div className="flex gap-4">
          <div className="flex min-w-32 gap-2 items-center flex-1 text-base font font-semibold">
            <span>
              <Text>{t("Cost Database")}</Text>
            </span>

            <IconComponent
              name="details"
              position="bottom"
              toolTipContent={t(
                "Ensure that the Cost Database that you want to use for this estimate is in the same currency as defined in the settings of this Project. Also, atleast one version of the Cost Database must be published for it to be visible in this list.",
              )}
              width="1.25rem"
              height="1.25rem"
            />
          </div>
          <div className="flex flex-4" data-testid="costDatabaseDropdownField">
            {getCostDatabaseDropdown()}
          </div>
        </div>
        {estimateCostDatabaseId && (
          <div className="flex gap-4">
            <div className="min-w-32 flex gap-2 items-center flex-1 text-base font font-semibold">
              <Text>{t("Cost Index")}</Text>

              <IconComponent
                name="details"
                position="bottom"
                toolTipContent={t(
                  "This value will be used to multiply the rate of every item from a cost database. It will not affect items that are added directly to the estimate.",
                )}
                width="1.25rem"
                height="1.25rem"
              />
            </div>
            <div className="flex flex-4">
              <TextFieldComponent
                type="number"
                placeholder={t("Enter Value")}
                defaultValue={estimateCostIndex}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEstimateCostIndex(Number(event.target.value))
                }
                data-testid="costIndexInputField"
              />
            </div>
          </div>
        )}
        <div className="flex gap-4">
          <div
            className="min-w-32 items-center flex flex-1 text-base font font-semibold"
            data-testid="descriptionLabel"
          >
            <Text>{t("Description")}</Text>
          </div>
          <div className="flex flex-4">
            <textarea
              className="mb-1.5 w-full resize-none border border-[#19204880] px-2 py-[0.42rem] outline-none placeholder:text-[#19204880] focus:border-[#0078d4]"
              // type="textarea"
              rows={5}
              minLength={1}
              maxLength={255}
              name="description"
              placeholder={t("Estimate Description")}
              value={estimateDescription}
              onChange={(event) => setEstimateDescription(event.target.value)}
              data-testid="descriptionTextArea"
            />
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex items-center min-w-32 flex-1 text-base font font-semibold">
            <Text>{t("Permissions")}</Text>
            <span className="text-2xl align-middle font-light text-red-500">*</span>
          </div>
          <div className="flex flex-4">
            <div className="flex gap-5">
              <RadioButton
                onChange={handlePermission}
                checked={inheritPermission}
                label={t("Inherit permissions from parent folder")}
              />
              <RadioButton
                onChange={handlePermission}
                checked={!inheritPermission}
                label={t("Specify permissions in the next step")}
              />
            </div>
          </div>
        </div>
      </div>
    );

    const modalFooterContent = () => (
      <div className="w-full flex gap-5 px-6 py-4">
        <ButtonComponentWithTestId
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          disabled={
            !estimateName.trim() ||
            !estimateSpaceId ||
            (Boolean(estimateCostDatabaseId) && Number(estimateCostIndex) <= 0)
          }
          onClick={handleCreateEstimate}
          data-testid="estimateCreateBtn"
        >
          <Text>{inheritPermission || showPermissionsFlow ? t("Create") : t("Next")}</Text>
        </ButtonComponentWithTestId>

        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={cancelCreateModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    const modalHeader = () => (
      <div
        className="flex ps-6 pe-2 py-4 items-center w-full justify-between bg-button_light"
        data-testid="createEstimateHeader"
      >
        {showPermissionsFlow ? (
          <div className="flex gap-2 items-center">
            <IconComponent
              name="BackwardArrow"
              width="1rem"
              height="1rem"
              className="cursor-pointer"
              onClick={() => setShowPermissionsFlow(false)}
            />
            <p className="flex items-center text-2xl font-bold" data-testid="createEstimateTitle">
              <Text>{t("Specify Permissions")}</Text>
            </p>
          </div>
        ) : (
          <div className="flex w-full gap-2 items-center justify-between">
            <p className="flex items-center text-2xl font-bold" data-testid="createEstimateTitle">
              <Text>{t("Create Estimate")}</Text>
            </p>
            <SelectSpaces
              moduleName={defaultModulesMapper.ESTIMATES}
              data-testid="spaceDropdownField"
              projectId={projectId}
              selectedSpaceCallback={(params: { _id: ObjectIdString }) => {
                setEstimateSpaceId(params._id);
              }}
            />
          </div>
        )}
      </div>
    );

    const getUsersCallBack = (args: EstimateUsersRoles) => {
      setUsersRoles(args);
    };

    const modelContent = () => (
      <div className="border-0 border-b border-button_light px-6 py-5">
        {showPermissionsFlow ? (
          <PermissionFlow
            module={defaultModulesMapper.ESTIMATES}
            spaceId={estimateSpaceId}
            getUsersCallBack={getUsersCallBack}
          />
        ) : (
          createEstimateContent()
        )}
      </div>
    );

    const createEstimateModal = () => (
      <ModalStateDialogComponent
        footerContent={modalFooterContent()}
        headerTitle={modalHeader()}
        isVisible={openCreatePopUp}
        onCancel={cancelCreateModal}
        modelContent={modelContent()}
        modalPadding={0}
        width="60%"
      />
    );

    const cancelDeleteModal = () => {
      setOpenDeleteModal(false);
    };

    const handleDelete = async () => {
      setIsButtonLoading(true);
      const selectedRecords = dataGrid.current?.dataView.getAllSelectedItems() ?? [];
      if (!selectedRecords[0]?._id) {
        setIsButtonLoading(false);
        return;
      }
      const response = await deleteEstimate({ estimateId: selectedRecords[0]._id });
      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("Selected Estimate has been deleted successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid.current?.refresh?.();
        cancelDeleteModal();
      } else {
        dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
      }
      setIsButtonLoading(false);
    };

    const deleteFooter = () => (
      <div className="w-full flex gap-5">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleDelete} loading={isButtonLoading}>
          <Text>{t("Delete")}</Text>
        </ButtonComponent>
        <ButtonComponent
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={cancelDeleteModal}
          disabled={isButtonLoading}
        >
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    const deleteEstimateModal = () => (
      <ModalStateDialogComponent
        footerContent={deleteFooter()}
        headerTitle={t("Delete Estimate")}
        isVisible={openDeleteModal}
        onCancel={isButtonLoading ? undefined : cancelDeleteModal}
        modelContent={
          <Text sx={{ whiteSpace: "normal" }}>{t("Are you sure you want to delete the selected estimate?")}</Text>
        }
        width="28.125rem"
      />
    );

    const createdByTemplate: Formatter<EstimateListItem> = (_row, _cell, _value, _columnDef, args) => {
      const container = document.createElement("div");
      container.className = "flex items-center";
      if (args.createdBy?._id) {
        renderUserTag(container, { id: args.createdBy._id });
      }
      return container;
    };

    const gridEditTemplate = (field: keyof EditingRowValue) => (
      <TextFieldComponent
        onChange={(e: { target: { value: string } }) =>
          setEditingRowValue((prev) => {
            return { ...prev, [field]: e.target.value };
          })
        }
        defaultValue={editingRowValueRef.current[field] ?? ""}
      />
    );

    const [gridColumns] = useState([
      {
        field: "counter",
        name: t("Estimate ID"),
        id: "counter",
        textAlign: "Left",
        minWidth: 80,
      },
      {
        field: "name",
        name: t("Estimate Name"),
        id: "name",
        textAlign: "Left",
        filterType: "Text",
        minWidth: 170,
        editorTemplate: () => gridEditTemplate("name"),
      },
      {
        field: "description",
        name: t("Description"),
        id: "description",
        textAlign: "Left",
        filterType: "Text",
        clipMode: "EllipsisWithTooltip",
        _valueGetter: (params: EstimateListItem) =>
          params.estimateType == "merge" ? t("Merge Estimate") : params.description || "-",
        editorTemplate: (params: { args: EstimateListItem }) => {
          return params.args.estimateType == "merge" ? t("Merge Estimate") : gridEditTemplate("description");
        },
        minWidth: 170,
      },
      ...(portalList
        ? []
        : [
            {
              field: "costIndex",
              name: t("Cost Index"),
              id: "costIndex",
              textAlign: "Left",
              filterType: "Number",
              clipMode: "EllipsisWithTooltip",
              _valueGetter: (params: EstimateListItem) =>
                params.costIndex
                  ? getEstimateNumberValueBasedOnSettings(Number(params.costIndex), params, "quantity")
                  : "-",
              minWidth: 100,
            },
          ]),
      {
        field: "amount",
        name: t("Estimate Total"),
        id: "amount",
        textAlign: "Left",
        formatter: estimateTotalTemplate,
        minWidth: 150,
      },
      ...(portalList
        ? [
            {
              field: "projectId",
              name: t("Project ID"),
              id: "projectId",
              textAlign: "Left",
              minWidth: 100,
            },
            {
              field: "projectName",
              name: t("Project Description"),
              id: "projectName",
              textAlign: "Left",
              minWidth: 150,
            },
          ]
        : []),
      {
        field: "spaceName",
        name: t("Space Name"),
        id: "spaceName",
        textAlign: "Left",
        filterType: "Text",
        minWidth: 150,
      },
      {
        field: "publishedVersionNumber",
        name: t("Version"),
        id: "publishedVersionNumber",
        textAlign: "Left",
        clipMode: "EllipsisWithTooltip",
        _valueGetter: (params: EstimateListItem) =>
          params.publishedVersionNumber ? `v${params.publishedVersionNumber}` : "-",
        minWidth: 80,
      },
      {
        field: "costDatabase.name",
        name: t("Cost Database"),
        id: "costDatabase",
        textAlign: "Left",
        clipMode: "EllipsisWithTooltip",
        _valueGetter: (params: EstimateListItem) => _.get(params, "costDatabase.name") || "-",
        minWidth: 150,
      },
      {
        field: "createdBy.fullName",
        name: t("Created by"),
        id: "createdBy",
        textAlign: "Left",
        filterType: "Text",
        formatter: createdByTemplate,
        minWidth: 180,
      },
      {
        field: "createdDate",
        name: t("Created On"),
        id: "createdDate",
        textAlign: "Left",
        filterType: "Date",
        formatter: formatDate,
        minWidth: 150,
      },
    ]);

    // The grid hands `beforeDataBound` the raw remote payload — sometimes the whole axios-shaped
    // response, sometimes the already-unwrapped body — so it is taken at `unknown` and parsed here
    // rather than described by a shape nothing checks. A wider handler stays assignable to the prop.
    const mapEstimateListResponse = useCallback((response: unknown): EstimateListItem[] => {
      const raw = typeof response === "object" && response !== null && "data" in response ? response.data : response;
      const parsed = getEstimateListResponseSchema.safeParse(raw);
      if (!parsed.success || !parsed.data.status) {
        return [];
      }
      return parsed.data.result ?? [];
    }, []);

    const noDataIllustration = () => (
      <div className="border-t border-[#C6D8EC] flex flex-col gap-2 items-center justify-center w-full h-full overflow-auto">
        <IconComponent name="NoData" className="flex overflow-hidden" width="15rem" height="15rem" />
        <Text className="text-xl font-semibold text-text_light">{t("No Estimate has been added yet")}</Text>
      </div>
    );

    // The "Cost Database" and "Created by" columns address a NESTED field by path
    // ("costDatabase.name", "createdBy.fullName"), which can never be a `keyof EstimateListItem`, so
    // `typedColumns()` cannot mint this list. `withDynamicColumns` is SlickGrid1's brander for
    // exactly this mix; it returns the same array, so the prop's identity is unchanged.
    const gridColumnDefsProp = withDynamicColumns<EstimateListItem>(gridColumns);

    const getEstimatesGrid1 = () => (
      <div className="overflow-auto flex h-full w-full">
        <SlickGrid1<EstimateListItem>
          ref={dataGrid}
          enableSelectAllCheckbox
          selectCheckBoxOnRowClick={allowSingleSelection}
          selectAtmostOneCheckBoxOnRowClick={allowSingleSelection}
          showPageCount
          columnDefinitions={gridColumnDefsProp}
          disableCheckBoxSelection={allowSingleSelection}
          enableSortingOption
          onSelectedRowIdsChanged={selectionEvent}
          remoteDataSource={estimateListUrl}
          beforeDataBound={mapEstimateListResponse}
          query={getEstimateListQuery}
          infiniteScroll
          enableRowResize
          enableFilteringOption
          enableGroupingOption
          enableColumnChooser
          toolbar={showOnlyGrid ? undefined : toolbarOptions}
          toolbarClickHandler={toolbarClickHandler}
          onClick={recordClick}
          isEditMode={!!editingRowValue.estimateId}
          propertiesPanel={!showOnlyGrid}
          propertiesDisabled={toolbarButtonDisableState.properties}
          noDataIllustration={noDataIllustration}
        />
      </div>
    );

    return (
      <>
        {showOnlyGrid && currencyData.currencyDetail?.id && getEstimatesGrid1()}

        {!showOnlyGrid && (
          <GridPageLayout
            title={"Estimates" as TranslationKey}
            description={
              (portalList ? "List of Estimates in the Portal" : "List of Estimates in this project") as TranslationKey
            }
          >
            {openCreatePopUp && createEstimateModal()}
            {openDeleteModal && deleteEstimateModal()}
            <div className="flex overflow-hidden flex-col w-full">
              <div className="h-full overflow-hidden" id="estimatesList" data-testid="estimateList">
                {(portalList || currencyData.currencyDetail?.id) && getEstimatesGrid1()}
              </div>
            </div>
            {openPropertySidebar && (
              <CentralProperties
                selectedRows={selectedRowsCount}
                width={"40rem"}
                noSelectionMessage={t("No Estimate seleted")}
                multipleSelectionMessage={t("Multiple Estimates selected")}
              >
                {estimatePropertyDetail?._id && (
                  <EstimateProperty
                    source="estimate-list"
                    estimateprop={{ ...estimatePropertyDetail, _id: estimatePropertyDetail._id! }}
                    updateStateToRefreshDatasource={updateStateToRefreshDatasource}
                    needPageRefresh={true}
                  />
                )}
              </CentralProperties>
            )}
          </GridPageLayout>
        )}
      </>
    );
  },
);
EstimateList.displayName = "EstimateList";
export default EstimateList;
